import { z } from "zod";
import { moneySchema, quantitySchema, timestampSchema, uuidSchema } from "../common/primitives.js";
import { posLineKindSchema } from "../pos/enums.js";
import { FLEET_VIOLATION_CODES, fleetPolicySchema, fleetTimeWindowSchema, fleetUsageSchema, fleetViolationActionSchema, } from "./enums.js";
/**
 * Filo kuralı (ADR 0017 §12): carinin VARSAYILANI + aracın GEÇERSİZ KILMASI. Katmanda her alan `null` = üst katmandan
 * (ya da motor varsayılanından) gelir; boş dizi kısıt DEĞİLDİR. Etkin kural `mergeFleetPolicies(varsayılan, araç)`'tır.
 *
 * Saf değerlendirme `@station/pricing-engine`'dedir ve sunucu / kasa / web aynı kodu kullanır; API bu paketi
 * sözleşmeler üzerinden alır (motor bağımlılığı `@station/contracts`'tadır).
 */
export { applyFleetUsage, evaluateFleetAuthorization, FLEET_POLICY_DEFAULTS, FLEET_WARNING_CODES, mergeFleetPolicies, NON_OVERRIDABLE_FLEET_VIOLATIONS, } from "@station/pricing-engine";
const positiveQuantity = quantitySchema.refine((value) => Number(value) > 0, "Limit sıfırdan büyük olmalıdır");
const positiveMoney = moneySchema.refine((value) => !value.startsWith("-") && BigInt(value) > 0n, "Limit sıfırdan büyük olmalıdır");
/** Kural katmanı alanları (girdi): `null` = üstten gelir. */
export const fleetPolicyLayerFieldsSchema = z.object({
    allowedProductIds: z.array(uuidSchema).max(200).nullable().default(null),
    allowedStationIds: z.array(uuidSchema).max(500).nullable().default(null),
    timeWindows: z
        .array(fleetTimeWindowSchema)
        .max(14)
        .nullable()
        .default(null)
        .describe("İzinli gün × saat pencereleri [from, to) — İstanbul yerel saati; boş = her saat"),
    dailyLitreLimit: positiveQuantity.nullable().default(null),
    monthlyLitreLimit: positiveQuantity.nullable().default(null),
    dailyAmountLimit: positiveMoney.nullable().default(null).describe("Kuruş"),
    monthlyAmountLimit: positiveMoney.nullable().default(null).describe("Kuruş"),
    requireOdometer: z.boolean().nullable().default(null),
    requireDriverCard: z.boolean().nullable().default(null),
    violationAction: fleetViolationActionSchema.nullable().default(null),
});
/** PUT gövdesi: katmanın TAMAMI değişir; `version` = okunan sürüm (kayıt yoksa 0). */
export const saveFleetPolicyLayerSchema = fleetPolicyLayerFieldsSchema.extend({
    version: z.number().int().min(0).describe("İyimser kilit; katman henüz yoksa 0"),
});
export const fleetPolicyLayerSchema = fleetPolicyLayerFieldsSchema.extend({
    scope: z.enum(["COUNTERPARTY", "VEHICLE"]),
    counterpartyId: uuidSchema,
    vehicleId: uuidSchema.nullable(),
    /** Kayıt yoksa 0 (tüm alanlar null = kısıt yok / üstten gelir). */
    version: z.number().int().min(0),
    updatedAt: timestampSchema.nullable(),
});
export const fleetRemainingSchema = z.object({
    dayLitres: quantitySchema.nullable(),
    monthLitres: quantitySchema.nullable(),
    dayAmount: moneySchema.nullable(),
    monthAmount: moneySchema.nullable(),
});
/** Aracın ETKİN kuralı + kullanım defterindeki GERÇEK kullanım (İstanbul günü / ayı). */
export const fleetEffectivePolicySchema = z.object({
    vehicleId: uuidSchema.nullable(),
    counterpartyId: uuidSchema,
    policy: fleetPolicySchema,
    defaultLayer: fleetPolicyLayerSchema,
    vehicleLayer: fleetPolicyLayerSchema.nullable(),
    usage: fleetUsageSchema,
    remaining: fleetRemainingSchema.describe("Limit yoksa null; aşılmışsa 0"),
});
// ── Yetkilendirme önizlemesi ───────────────────────────────────────────────
export const fleetAuthorizationPreviewRequestSchema = z.object({
    stationId: uuidSchema,
    at: timestampSchema.optional().describe("Yoksa şimdi"),
    lines: z
        .array(z.object({
        productId: uuidSchema,
        kind: posLineKindSchema.optional().describe("Yoksa ürün türünden türetilir (akaryakıt / LPG → FUEL)"),
        quantity: quantitySchema,
    }))
        .max(100)
        .default([]),
    amount: moneySchema.describe("Filoya yazılacak tutar (kuruş)"),
    odometerKm: z.number().int().min(0).max(9_999_999).nullable().optional(),
    driverCardPresented: z.boolean().default(false),
});
export const fleetViolationSchema = z.object({
    code: z.enum(FLEET_VIOLATION_CODES),
    message: z.string(),
    overridable: z.boolean(),
    meta: z.record(z.string(), z.string()),
});
export const fleetAuthorizationResultSchema = z.object({
    allowed: z.boolean(),
    action: z.enum(["NONE", "BLOCK", "APPROVAL"]),
    violations: z.array(fleetViolationSchema),
    warnings: z.array(z.enum(["ODOMETER_DECREASED"])),
    usageBefore: z.object({
        day: z.string(),
        month: z.string(),
        dayLitres: quantitySchema,
        dayAmount: moneySchema,
        monthLitres: quantitySchema,
        monthAmount: moneySchema,
    }),
    remainingBefore: fleetRemainingSchema,
    maxLitres: quantitySchema.nullable(),
    requested: z.object({ litres: quantitySchema, amount: moneySchema }),
});
export const fleetAuthorizationPreviewSchema = z.object({
    vehicleId: uuidSchema,
    counterpartyId: uuidSchema,
    policy: fleetPolicySchema,
    result: fleetAuthorizationResultSchema,
});
/**
 * Elle girilen filo veresiye fişine (vardiya) DONDURULAN karar — `shift_credit_sales.fleet_authorization`.
 * `verdict = OVERRIDDEN` → ihlal `fleet.rule.override` (step-up) ile geçildi.
 */
export const fleetSlipAuthorizationSchema = z.object({
    verdict: z.enum(["ALLOWED", "OVERRIDDEN"]),
    source: z.literal("SERVER"),
    action: z.enum(["NONE", "BLOCK", "APPROVAL"]),
    violations: z.array(fleetViolationSchema),
    warnings: z.array(z.string()),
    usageBefore: fleetAuthorizationResultSchema.shape.usageBefore,
    evaluatedAt: timestampSchema,
});
//# sourceMappingURL=policies.js.map